import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowCounterClockwise, PencilSimple, Path, Trash, Info, ArrowRight, X } from '@phosphor-icons/react';
import {
  getUserRoutes, deleteUserRoute, updateUserRouteStops,
  getRouteProgress, saveRouteProgress, resetRouteProgress,
} from '../services/api';
import { useCollections } from '../context/CollectionsContext';
import { useFavorites } from '../context/FavoritesContext';
import telegramService from '../services/telegram';
import Loader from '../components/Loader';
import BottomSheet from '../components/BottomSheet';
import WalkScreen, { ConfirmSheet } from '../components/walk/WalkScreen';
import WalkFinish from '../components/walk/WalkFinish';
import RouteEditor from '../components/route-editor/RouteEditor';
import PlacePicker from '../components/route-editor/PlacePicker';
import { toRouteStop } from '../components/CreateRouteWizard';
import useWalkGeolocation from '../hooks/useWalkGeolocation';
import { getCurrentDayStatus } from '../utils/workingHours';
import { findStartStation } from '../utils/metro';
import { orderByProximity, straightLength } from '../utils/routeOrder';
import { plural } from '../utils/plural';
import { calcSteps, calcDistance, formatDist, formatElapsed } from '../utils/walk';
import { notify, notifyIcons } from '../utils/notify';

const USER_ROUTE_PROGRESS_KEY = 'user_route_progress_';
const USER_ROUTE_TIMER_KEY = 'user_route_timer_start_';

// С какого расстояния до места предлагать пойти сначала в другое
const FAR_STOP_M = 1500;
// Насколько ближе должно быть другое место, чтобы перестановка имела смысл
const REORDER_GAIN = 0.6;

// Поля, которые сервер хранит в `stops`. Подмешанные на чтении
// (описание, часы, фото) обратно не отправляем
const toStoredStop = ({ title, placeId, latitude, longitude, metro, address, imageUrl, slug }, i) => ({
  order: i + 1, title, placeId, latitude, longitude, metro, address, imageUrl, slug,
});

const isPassed = (st) => st === 'done' || st === 'skipped';

/**
 * Место своего маршрута — в общий вид экрана прогулки (WalkScreen).
 * Описание и фото приходят из place: контроллер user-route.ts
 * подмешивает их на каждом запросе
 */
const toWalkStop = (stop, i, status) => {
  const gallery = stop.images?.length ? stop.images : (stop.imageUrl ? [stop.imageUrl] : []);
  return {
    // Ключ — место, а не позиция: при перестановке метки и строки
    // не пересоздаются посреди анимации карты
    key: String(stop.placeId ?? `i${i}`),
    status,
    title: stop.title,
    point: stop.latitude != null && stop.longitude != null ? [stop.latitude, stop.longitude] : null,
    thumb: stop.imageUrl || gallery[0] || null,
    images: gallery.length ? gallery.map((url, k) => ({ id: k, url })) : null,
    // В описаниях мест встречается разметка — на прогулке она лишняя
    description: stop.description ? stop.description.replace(/[#*_[\]]/g, '') : null,
    workingHours: stop.workingHours,
    address: stop.address,
    slug: stop.slug,
  };
};

/**
 * Прогулка по своему маршруту — маршрут собран из избранного.
 *
 * Экран прогулки общий с маршрутом редакции (WalkScreen); здесь — своё:
 * прогресс, редактор маршрута прямо на ходу, подсказки «идти далеко,
 * рядом есть…» и «сегодня закрыто», перестроение порядка и удаление.
 *
 * Прогресс хранится как раньше — `{ completed, skipped, currentStop }`
 * локально и `statuses` на сервере: начатые прогулки не теряются
 */
const UserRouteWalkingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { removeUserRoute, updateUserRoute } = useCollections();
  const { favorites } = useFavorites();
  const geo = useWalkGeolocation();

  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  // Статус каждого места по порядку: done | skipped | upcoming
  const [statuses, setStatuses] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [finishedMs, setFinishedMs] = useState(null);
  // Редактор маршрута: черновик правки, режим «добавить места»
  const [draft, setDraft] = useState(null);
  const [addMode, setAddMode] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showReorder, setShowReorder] = useState(false);
  const [showRestart, setShowRestart] = useState(false);
  // Место, на котором подсказку «идти далеко» уже отклонили — второй
  // раз не спрашиваем
  const [reorderDismissed, setReorderDismissed] = useState(null);
  const timerRef = useRef(null);
  // Прогресс поднят — до этого сохранять нельзя, иначе пустое состояние
  // затрёт серверное
  const progressLoadedRef = useRef(false);
  const restartingRef = useRef(false);
  const deletedRef = useRef(false);

  // ── Загрузка маршрута и прогресса ──
  useEffect(() => {
    const load = async () => {
      if (deletedRef.current) return;
      try {
        const res = await getUserRoutes();
        const found = (res.data || []).find((r) => String(r.id) === String(id));
        if (!found) {
          if (!deletedRef.current) navigate('/favorites', { replace: true });
          return;
        }
        // SQLite может вернуть координаты метро строкой
        if (found.startMetroLat != null) found.startMetroLat = parseFloat(found.startMetroLat);
        if (found.startMetroLng != null) found.startMetroLng = parseFloat(found.startMetroLng);
        setRoute(found);
        const n = (found.stops || []).length;

        // Источник истины — сервер: цифры не должны гибнуть при чистке
        // кеша и расходиться на разных устройствах. localStorage остаётся
        // запасным вариантом, сеть на прогулке ненадёжна
        const remote = await getRouteProgress({ userRouteId: Number(id) });
        let next = null;
        if (remote?.statuses?.length) {
          next = {
            statuses: remote.statuses.slice(0, n),
            index: remote.currentIndex ?? 0,
          };
          if (remote.startedAt) {
            localStorage.setItem(USER_ROUTE_TIMER_KEY + id, String(new Date(remote.startedAt).getTime()));
          }
          if (remote.elapsedMs) setFinishedMs(Number(remote.elapsedMs));
        } else {
          try {
            const saved = JSON.parse(localStorage.getItem(USER_ROUTE_PROGRESS_KEY + id) || 'null');
            if (saved) {
              next = {
                statuses: Array.from({ length: n }, (_, i) => (
                  saved.completed?.includes(i) ? 'done' : saved.skipped?.includes(i) ? 'skipped' : 'upcoming'
                )),
                index: saved.currentStop || 0,
              };
            }
          } catch {}
        }
        const st = Array.from({ length: n }, (_, i) => next?.statuses?.[i] || 'upcoming');
        setStatuses(st);
        // Текущее — первое непройденное: индекс с сервера мог устареть
        // после правки маршрута на другом устройстве
        const firstOpen = st.findIndex((s) => !isPassed(s));
        setCurrentIndex(firstOpen === -1 ? n : firstOpen);
      } catch {
        if (!deletedRef.current) navigate('/favorites', { replace: true });
      } finally {
        progressLoadedRef.current = true;
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const stops = useMemo(() => route?.stops || [], [route]);
  const allDone = stops.length > 0 && statuses.length === stops.length && statuses.every(isPassed);

  // ── Сохранение прогресса ──
  // Локально — всегда: сеть на прогулке ненадёжна. На сервер — чтобы
  // цифры не гибли при чистке кеша и совпадали на разных устройствах
  useEffect(() => {
    if (!route || restartingRef.current || !progressLoadedRef.current) return;
    const completed = [];
    const skipped = [];
    statuses.forEach((s, i) => { if (s === 'done') completed.push(i); else if (s === 'skipped') skipped.push(i); });
    localStorage.setItem(
      USER_ROUTE_PROGRESS_KEY + id,
      JSON.stringify({ completed, skipped, currentStop: currentIndex })
    );
    const startedTs = parseInt(localStorage.getItem(USER_ROUTE_TIMER_KEY + id) || '0', 10);
    saveRouteProgress({
      userRouteId: Number(id),
      statuses,
      currentIndex,
      ...(startedTs ? { startedAt: new Date(startedTs).toISOString() } : {}),
    });
  }, [statuses, currentIndex, id, route]);

  // ── Таймер прогулки ──
  useEffect(() => {
    if (!route || allDone) return undefined;
    const startTs = parseInt(localStorage.getItem(USER_ROUTE_TIMER_KEY + id) || '0', 10);
    if (!startTs) localStorage.setItem(USER_ROUTE_TIMER_KEY + id, Date.now().toString());
    timerRef.current = setInterval(() => {
      const ts = parseInt(localStorage.getItem(USER_ROUTE_TIMER_KEY + id) || Date.now().toString(), 10);
      setElapsedMs(Date.now() - ts);
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [id, route, allDone]);

  // Следующее непройденное после `from` — после правки порядка
  // пропущенные могут оказаться и впереди, и позади
  const nextOpen = (list, from) => {
    for (let i = from; i < list.length; i++) if (!isPassed(list[i])) return i;
    const any = list.findIndex((s) => !isPassed(s));
    return any === -1 ? list.length : any;
  };

  // Текущее место — в «пройдено» или «пропущено», дальше следующее.
  // Последнее — фиксируем итог: статусы шлём вместе с ним, иначе
  // эффект сохранения мог успеть записать старое состояние
  const leaveCurrent = useCallback((status) => {
    const next = statuses.map((s, i) => (i === currentIndex ? status : s));
    setStatuses(next);
    const idx = nextOpen(next, currentIndex + 1);
    setCurrentIndex(idx);
    if (next.every(isPassed)) {
      clearInterval(timerRef.current);
      const startedTs = parseInt(localStorage.getItem(USER_ROUTE_TIMER_KEY + id) || '0', 10);
      const finalMs = startedTs ? Date.now() - startedTs : elapsedMs;
      setFinishedMs(finalMs);
      saveRouteProgress({
        userRouteId: Number(id),
        statuses: next,
        currentIndex: next.length,
        finishedAt: new Date().toISOString(),
        elapsedMs: finalMs,
      });
    }
  }, [statuses, currentIndex, id, elapsedMs]);

  // Отметил по ошибке — вернуться к последнему пройденному перед текущим
  const goBack = useCallback(() => {
    let prev = -1;
    for (let i = currentIndex - 1; i >= 0; i--) if (isPassed(statuses[i])) { prev = i; break; }
    if (prev === -1) return;
    setStatuses((list) => list.map((s, i) => (i === prev ? 'upcoming' : s)));
    setCurrentIndex(prev);
  }, [currentIndex, statuses]);

  // ── Сохранение нового порядка / состава ──
  // Статусы переносим по месту, а не по номеру: после перестановки
  // «второе» — это уже другое место
  const applyStops = useCallback(async (newStops, extra = {}) => {
    const byPlace = new Map(stops.map((s, i) => [s.placeId, statuses[i]]));
    const newStatuses = newStops.map((s) => byPlace.get(s.placeId) || 'upcoming');
    const nextRoute = { ...route, ...extra, stops: newStops };
    setRoute(nextRoute);
    setStatuses(newStatuses);
    setCurrentIndex(nextOpen(newStatuses, 0));
    updateUserRoute({ id: route.id, ...extra, stops: newStops });
    try {
      await updateUserRouteStops(id, newStops.map(toStoredStop), extra);
      return true;
    } catch {
      notify.error('Не удалось сохранить. Проверьте связь');
      return false;
    }
  }, [stops, statuses, route, id, updateUserRoute]);

  // ── Перестроение от текущей позиции ──
  // Пройденные остаются сверху, остальное — «ближайшее следующим»
  const reorderFromHere = useCallback(async () => {
    const userPosition = geo.userPosition;
    if (!userPosition) return;
    telegramService.hapticFeedback('light');
    const passed = stops.filter((_, i) => isPassed(statuses[i]));
    const open = stops.filter((_, i) => !isPassed(statuses[i]));
    const ok = await applyStops([...passed, ...orderByProximity(open, userPosition)]);
    if (ok) notify('Порядок перестроен', { icon: notifyIcons.done });
  }, [geo.userPosition, stops, statuses, applyStops]);

  // ── Редактор маршрута ──
  const openEditor = () => {
    const passedFirst = [
      ...stops.filter((_, i) => statuses[i] === 'done'),
      ...stops.filter((_, i) => statuses[i] !== 'done'),
    ];
    const station = route.startType === 'metro' && route.startMetroName
      ? { name: route.startMetroName, lat: route.startMetroLat, lng: route.startMetroLng }
      : null;
    setDraft({
      stops: passedFirst.map((s) => ({ ...s, thumb: s.imageUrl })),
      title: route.title,
      station,
      lockedCount: statuses.filter((s) => s === 'done').length,
    });
    setAddOpen(false);
  };
  const saveDraft = async () => {
    if (!draft || savingDraft) return;
    setSavingDraft(true);
    const extra = { title: draft.title.trim() || route.title };
    if (draft.station) {
      Object.assign(extra, {
        startType: 'metro',
        startMetroName: draft.station.name,
        startMetroLat: draft.station.lat,
        startMetroLng: draft.station.lng,
      });
    }
    const ok = await applyStops(draft.stops.map(({ thumb, ...s }) => s), extra);
    setSavingDraft(false);
    if (ok) {
      telegramService.hapticFeedback('success');
      setDraft(null);
      notify('Маршрут сохранён', { icon: notifyIcons.done });
    }
  };
  const confirmAdd = () => {
    const known = new Set(draft.stops.map((s) => s.placeId));
    const added = favorites
      .map((f) => f.place)
      .filter((p) => p && addMode.includes(p.id) && !known.has(p.id))
      .sort((a, b) => addMode.indexOf(a.id) - addMode.indexOf(b.id))
      .map(toRouteStop);
    setDraft({ ...draft, stops: [...draft.stops, ...added] });
    setAddMode([]);
    setAddOpen(false);
  };

  // ── Сброс и удаление ──
  const handleRestart = async () => {
    restartingRef.current = true;
    localStorage.removeItem(USER_ROUTE_PROGRESS_KEY + id);
    localStorage.setItem(USER_ROUTE_TIMER_KEY + id, Date.now().toString());
    // Серверную запись тоже: иначе прогресс вернётся при следующей загрузке
    await resetRouteProgress({ userRouteId: Number(id) });
    setStatuses(stops.map(() => 'upcoming'));
    setCurrentIndex(0);
    setElapsedMs(0);
    setFinishedMs(null);
    restartingRef.current = false;
  };
  const handleDelete = async () => {
    deletedRef.current = true;
    setShowDelete(false);
    try {
      await deleteUserRoute(id);
      telegramService.hapticFeedback('success');
      // id из URL — строка, в списке число
      removeUserRoute(Number(id));
    } catch {}
    localStorage.removeItem(USER_ROUTE_PROGRESS_KEY + id);
    navigate('/favorites', { replace: true });
  };

  if (loading) return <Loader />;
  if (!route) return null;

  const walkStops = stops.map((s, i) => toWalkStop(
    s, i, i === currentIndex && !isPassed(statuses[i]) ? 'active' : (statuses[i] || 'upcoming')
  ));
  const points = walkStops.map((s) => s.point).filter(Boolean);
  const routeKm = Number((straightLength(points) / 1000).toFixed(1));

  if (allDone) {
    const doneCount = statuses.filter((s) => s === 'done').length;
    const skippedCount = statuses.filter((s) => s === 'skipped').length;
    const steps = calcSteps(routeKm);
    const ms = finishedMs ?? elapsedMs;
    const results = [
      ms > 0 && { value: formatElapsed(ms), label: 'в пути' },
      routeKm > 0 && { value: `~${String(routeKm).replace('.', ',')} км`, label: 'пройдено' },
      steps && { value: `~${steps.toLocaleString('ru-RU')}`, label: 'шагов' },
      {
        value: `${doneCount} из ${stops.length}`,
        label: skippedCount > 0
          ? `${plural(stops.length, ['места', 'мест', 'мест'])}, ${skippedCount} пропущено`
          : plural(stops.length, ['места', 'мест', 'мест']),
      },
    ].filter(Boolean);
    const share = () => {
      telegramService.hapticFeedback('light');
      // Свой маршрут личный, ссылки на него нет — делимся приложением
      // Текст уходит в Telegram, а не в интерфейс — символ тут допустим (брендбук 9.3)
      const text = `Прошёл свой маршрут по Петербургу: ${doneCount} ${plural(doneCount, ['место', 'места', 'мест'])}${ms > 0 ? ` за ${formatElapsed(ms)}` : ''} 🚶`;
      telegramService.openTelegramLink(
        `https://t.me/share/url?url=${encodeURIComponent('https://t.me/spbneformal_app_bot/apps')}&text=${encodeURIComponent(text)}`
      );
    };
    return (
      <WalkFinish
        title={route.title}
        results={results}
        stops={walkStops}
        onShare={share}
        secondary={{ label: 'Мои маршруты', onClick: () => navigate('/favorites') }}
        onRestart={handleRestart}
      />
    );
  }

  const current = stops[currentIndex];
  const station = route.startType === 'metro' && route.startMetroName && route.startMetroLat
    ? { name: route.startMetroName, lat: route.startMetroLat, lng: route.startMetroLng }
    : findStartStation(stops[0]);

  // ── Подсказки в шапке панели ──
  // «Идти далеко, рядом есть…» — турист выбирает места, не зная города,
  // и следующее запросто оказывается в другом конце. Предлагаем
  // переставить, а не удалить: «далеко» почти всегда значит «не сейчас»
  const userPosition = geo.userPosition;
  const distanceToCurrent = userPosition && current?.latitude != null
    ? calcDistance(userPosition[0], userPosition[1], current.latitude, current.longitude)
    : null;
  let reorderHint = null;
  if (distanceToCurrent !== null && distanceToCurrent >= FAR_STOP_M && reorderDismissed !== currentIndex) {
    stops.forEach((s, i) => {
      if (i === currentIndex || isPassed(statuses[i]) || s.latitude == null) return;
      const d = calcDistance(userPosition[0], userPosition[1], s.latitude, s.longitude);
      if (!reorderHint || d < reorderHint.dist) reorderHint = { index: i, dist: d, title: s.title };
    });
    if (reorderHint && reorderHint.dist > distanceToCurrent * REORDER_GAIN) reorderHint = null;
  }
  // Сегодня закрыто — идти до закрытой двери незачем, но решает человек
  const hours = current?.workingHours ? getCurrentDayStatus(current.workingHours) : null;
  const closedToday = hours?.status === 'closed';

  const goThereFirst = () => {
    if (!reorderHint) return;
    telegramService.hapticFeedback('light');
    const next = [...stops];
    const [item] = next.splice(reorderHint.index, 1);
    next.splice(currentIndex, 0, item);
    applyStops(next);
  };

  const notice = reorderHint ? (
    <Notice
      text={`Идти далеко. Рядом «${reorderHint.title}» — ${formatDist(reorderHint.dist)}`}
      action="Сначала туда"
      onAction={goThereFirst}
      onDismiss={() => setReorderDismissed(currentIndex)}
    />
  ) : closedToday ? (
    <Notice text={hours.message || 'Сегодня закрыто'} action="Пропустить" onAction={() => leaveCurrent('skipped')} />
  ) : null;

  const pendingCount = statuses.filter((s) => !isPassed(s)).length;
  const menuExtra = [
    { icon: PencilSimple, label: 'Изменить маршрут', onClick: openEditor },
    userPosition && pendingCount > 1 && { icon: Path, label: 'Перестроить от меня', onClick: () => setShowReorder(true) },
    statuses.some(isPassed) && { icon: ArrowCounterClockwise, label: 'Начать заново', onClick: () => setShowRestart(true) },
    { icon: Trash, label: 'Удалить маршрут', onClick: () => setShowDelete(true), danger: true },
  ].filter(Boolean);

  return (
    <>
      <WalkScreen
        stops={walkStops}
        currentIndex={currentIndex}
        elapsedMs={elapsedMs}
        geo={geo}
        startStation={station}
        onArrived={() => leaveCurrent('done')}
        onSkip={() => leaveCurrent('skipped')}
        onBack={goBack}
        menuExtra={menuExtra}
        notice={notice}
      />

      {/* Редактор маршрута — тот же экран, что второй шаг мастера.
          «Добавить места» — внутри этой же панели, не второй поверх */}
      <BottomSheet
        isOpen={!!draft}
        onClose={() => setDraft(null)}
        title={addOpen ? 'Добавить места' : 'Изменить маршрут'}
        subtitle={addOpen ? 'Из избранного' : undefined}
        closeOnBackdrop={false}
        contentClassName="pt-3"
        footer={draft && (addOpen ? (
          <div className="flex gap-2">
            <button
              onClick={() => { setAddMode([]); setAddOpen(false); }}
              className="flex h-14 flex-1 items-center justify-center rounded-md border-0 bg-theme-card-secondary text-label text-theme-text-secondary"
            >
              Отмена
            </button>
            <button
              onClick={confirmAdd}
              disabled={addMode.length === 0}
              className="flex h-14 flex-[2] items-center justify-center rounded-md border-0 bg-action text-label text-on-action disabled:opacity-50"
            >
              {addMode.length ? `Добавить · ${addMode.length}` : 'Выберите места'}
            </button>
          </div>
        ) : (
          <button
            onClick={saveDraft}
            disabled={savingDraft || draft.stops.length < 2}
            className="flex h-14 w-full items-center justify-center rounded-md border-0 bg-action text-label text-on-action transition-transform duration-fast ease-out active:scale-98 disabled:opacity-50"
          >
            {savingDraft ? 'Сохраняем…' : 'Сохранить'}
          </button>
        ))}
      >
        {draft && (addOpen ? (
          <PlacePicker
            favorites={favorites}
            selectedIds={addMode}
            lockedIds={draft.stops.map((s) => s.placeId)}
            onToggle={(place) => setAddMode((prev) => (
              prev.includes(place.id) ? prev.filter((x) => x !== place.id) : [...prev, place.id]
            ))}
          />
        ) : (
          <RouteEditor
            stops={draft.stops}
            onChange={(next) => setDraft({ ...draft, stops: next })}
            station={draft.station}
            onStationChange={(st) => setDraft({ ...draft, station: st })}
            title={draft.title}
            onTitleChange={(t) => setDraft({ ...draft, title: t })}
            lockedCount={draft.lockedCount}
            onAddPlaces={() => { setAddMode([]); setAddOpen(true); }}
          />
        ))}
      </BottomSheet>

      <ConfirmSheet
        isOpen={showReorder}
        onClose={() => setShowReorder(false)}
        title="Перестроить порядок?"
        action="Перестроить"
        onAction={() => { setShowReorder(false); reorderFromHere(); }}
      >
        Оставшиеся места выстроятся от вашего местоположения: ближайшее —
        следующим. Пройденные останутся как есть.
      </ConfirmSheet>

      <ConfirmSheet
        isOpen={showRestart}
        onClose={() => setShowRestart(false)}
        title="Начать заново?"
        action="Начать заново"
        danger
        onAction={() => { setShowRestart(false); handleRestart(); }}
      >
        Отметки и время прогулки сбросятся. Сам маршрут останется.
      </ConfirmSheet>

      <ConfirmSheet
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        title="Удалить маршрут?"
        action="Удалить"
        danger
        onAction={handleDelete}
      >
        Маршрут и его прогресс удалятся. Места останутся в избранном.
      </ConfirmSheet>
    </>
  );
};

/**
 * Подсказка в шапке панели прогулки: строка текста и действие. Спокойная —
 * поверхность и обычный текст, синим только действие: главная кнопка
 * «Я на месте» ниже не должна с ней спорить
 */
const Notice = ({ text, action, onAction, onDismiss }) => (
  <div className="mt-3 flex items-center gap-2 rounded-md bg-theme-bg-secondary py-1 pl-3 pr-1">
    <Info size={18} className="shrink-0 text-theme-text-secondary" />
    <span className="min-w-0 flex-1 text-body-sm text-theme-text-primary">{text}</span>
    <button
      onClick={onAction}
      className="flex h-10 shrink-0 items-center gap-1 rounded-md border-0 bg-transparent px-2 text-label text-action-text"
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      {action}
      <ArrowRight size={16} weight="bold" />
    </button>
    {onDismiss && (
      <button
        onClick={onDismiss}
        aria-label="Скрыть подсказку"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border-0 bg-transparent text-theme-text-secondary"
      >
        <X size={18} />
      </button>
    )}
  </div>
);

export default UserRouteWalkingPage;
